import { Modal } from "react-bootstrap";
import {
  Add,
  DeleteOutlineOutlined,
  Edit,
  GridView,
  Search,
  ImageOutlined,
  PhotoCamera,
  ArrowRightOutlined,
} from "@mui/icons-material";
import { IconButton, MenuItem, Paper, Select, Pagination } from "@mui/material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import React, { useEffect, useState, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeleteModal from "../../components/Modals/DeleteModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_API_URL = "https://estbn.com/EstbnNew/public/api/portal";

const PINNED_CATEGORY_ID = 64;

const EqCard = () => {
  const [viewMode, setViewMode] = useState("cards");
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewBy, setViewBy] = useState("Main Categories");
  const [categories, setCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageWasCleared, setImageWasCleared] = useState(false);

  const { user } = useSelector((state) => state.auth);
  const token = user?.token;

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      categoryName: "",
      categoryDescription: "",
      categoryBackground: "#F8F8F8",
    },
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreviewUrl(URL.createObjectURL(file));
      setImageWasCleared(false);
    } else {
      setSelectedImage(null);
      setImagePreviewUrl("");
    }
  };

  const handleClose = () => {
    setShow(false);
    reset({
      categoryName: "",
      categoryDescription: "",
      categoryBackground: "#F8F8F8",
    });
    setIsEditing(false);
    setCurrentCategory(null);
    setSelectedImage(null);
    setImagePreviewUrl("");
    setImageWasCleared(false);
  };

  const handleShowCreate = () => {
    setIsEditing(false);
    setCurrentCategory(null);
    reset({
      categoryName: "",
      categoryDescription: "",
      categoryBackground: "#F8F8F8",
    });
    setSelectedImage(null);
    setImagePreviewUrl("");
    setImageWasCleared(false);
    setShow(true);
  };

  const handleShowEdit = (categoryData) => {
    setIsEditing(true);
    setCurrentCategory(categoryData);
    reset({
      categoryName: categoryData.name,
      categoryDescription: categoryData.description || "",
      categoryBackground: categoryData.background_color || "#F8F8F8",
    });
    setSelectedImage(null);
    setImageWasCleared(false);
    if (categoryData.full_profile_image_url) {
      setImagePreviewUrl(categoryData.full_profile_image_url);
    } else {
      setImagePreviewUrl("");
    }
    setShow(true);
  };

  const handleShowDelete = (categoryData) => {
    setCategoryToDelete(categoryData);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  const handleDelete = async () => {
    if (!token || !categoryToDelete) {
      toast.error("Deletion failed: Missing token or category information.");
      return;
    }

    try {
      const response = await fetch(
        `${BASE_API_URL}/equipment-categories/${categoryToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(
          `Failed to delete category: ${errorData.message || "Unknown error"}`
        );
        return;
      }

      toast.success(
        `Category "${categoryToDelete.name}" deleted successfully!`
      );
      handleCloseDeleteModal();
      if (categories.length === 1 && paginationModel.page > 0) {
        setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }));
      } else {
        fetchCategories(paginationModel.page, paginationModel.pageSize);
      }
    } catch (error) {
      console.error("Network or API call error during deletion:", error);
      toast.error("Network error during deletion. Please try again.");
    }
  };

  const handleSearch = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleViewByChange = (event) => {
    const selectedValue = event.target.value;
    setViewBy(selectedValue);

    if (selectedValue === "Sub Categories") {
      if (categories.length > 0) {
        navigate(`/subcategory`);
      } else {
        toast.info("No main categories available to view subcategories for.");
      }
    } else if (selectedValue === "Models") {
      if (categories.length > 0) {
        navigate(`/EQmodal`);
      } else {
        toast.info("No main Models available to view models for.");
      }
    } else if (selectedValue === "All Equipment") {
      navigate(`/AllEquipments`);
    }
  };

  const fetchCategories = useCallback(
    async (page, pageSize, search = searchTerm) => {
      if (!token) {
        console.warn("Authorization token is not available. Skipping fetch.");
        setCategories([]);
        setRowCount(0);
        return;
      }
      try {
        const apiUrl = `${BASE_API_URL}/equipment-categories?type=main&page=${
          page + 1
        }&per_page=${pageSize}${search ? `&search=${search}` : ""}`;
        const response = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data.success && data.data && data.data.categories) {
          let fetchedCategories = data.data.categories;
          let total = data.data.pagination.total || 0;

          let pinnedCategoryData = null;
          const pinnedIndex = fetchedCategories.findIndex(
            (cat) => cat.id === PINNED_CATEGORY_ID
          );

          if (pinnedIndex !== -1) {
            pinnedCategoryData = fetchedCategories[pinnedIndex];
            fetchedCategories.splice(pinnedIndex, 1);
          }

          if (pinnedCategoryData) {
            fetchedCategories.unshift(pinnedCategoryData);
          }

          setCategories(fetchedCategories);
          setRowCount(total);
        } else {
          setCategories([]);
          setRowCount(0);
        }
      } catch (error) {
        console.error("Error fetching equipment categories:", error);
        toast.error("Failed to fetch categories.");
        setCategories([]);
        setRowCount(0);
      }
    },
    [token, searchTerm]
  );

  useEffect(() => {
    fetchCategories(paginationModel.page, paginationModel.pageSize);
  }, [fetchCategories, paginationModel.page, paginationModel.pageSize]);

  const uploadProfileImage = async (categoryId, imageFile) => {
    if (!token || !categoryId || !imageFile) {
      console.error("Missing token, category ID, or image file for upload.");
      toast.error("Image upload failed: Missing data.");
      return null;
    }

    const formData = new FormData();
    formData.append("profile_image", imageFile);

    try {
      const response = await fetch(
        `${BASE_API_URL}/equipment-categories/${categoryId}/profile-image`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(
          `Image upload failed: ${errorData.message || "Unknown error"}`
        );
        return null;
      }

      const responseData = await response.json();
      toast.success("Profile image uploaded successfully!");
      return responseData.data?.full_profile_image_url || null;
    } catch (error) {
      console.error("Network or API call error during image upload:", error);
      toast.error("Network error during image upload. Please try again.");
      return null;
    }
  };

  const deleteProfileImage = async (categoryId) => {
    if (!token || !categoryId) {
      console.error("Missing token or category ID for image deletion.");
      toast.error("Image deletion failed: Missing data.");
      return false;
    }

    try {
      const response = await fetch(
        `${BASE_API_URL}/equipment-categories/${categoryId}/profile-image`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(
          `Image deletion failed: ${errorData.message || "Unknown error"}`
        );
        return false;
      }

      toast.success("Profile image deleted successfully!");
      return true;
    } catch (error) {
      console.error("Network or API call error during image deletion:", error);
      toast.error("Network error during image deletion. Please try again.");
      return false;
    }
  };

  const onSubmit = async (formData) => {
    if (!token) {
      toast.error("Action failed: Authorization token is missing.");
      return;
    }

    const categoryDataToSend = {
      name: formData.categoryName,
      description: formData.categoryDescription || "",
      background_color: formData.categoryBackground,
    };

    let categoryApiUrl = `${BASE_API_URL}/equipment-categories`;
    let categoryMethod = "POST";
    let successMessage = "Category created successfully!";
    let currentCategoryId = null;

    if (isEditing && currentCategory) {
      categoryApiUrl = `${BASE_API_URL}/equipment-categories/${currentCategory.id}`;
      categoryMethod = "PUT";
      successMessage = "Category updated successfully!";
      currentCategoryId = currentCategory.id;
    }

    try {
      const categoryResponse = await fetch(categoryApiUrl, {
        method: categoryMethod,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(categoryDataToSend),
      });

      if (!categoryResponse.ok) {
        const errorData = await categoryResponse.json();
        toast.error(
          `Category operation failed: ${errorData.message || "Unknown error"}`
        );
        return;
      }

      const categoryResponseData = await categoryResponse.json();
      currentCategoryId =
        currentCategoryId || categoryResponseData.data?.category?.id;

      if (!currentCategoryId) {
        toast.error("Failed to get category ID for image operations.");
        return;
      }

      if (selectedImage) {
        await uploadProfileImage(currentCategoryId, selectedImage);
      } else if (
        isEditing &&
        imageWasCleared &&
        currentCategory?.full_profile_image_url
      ) {
        await deleteProfileImage(currentCategoryId);
      }

      toast.success(successMessage);
      handleClose();

      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      fetchCategories(0, paginationModel.pageSize);
    } catch (error) {
      toast.error("Network error. Please check your connection and try again.");
    }
  };

  const totalPages = Math.ceil(rowCount / paginationModel.pageSize);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
      renderCell: (params) => (
        <div className="d-flex align-items-center">{params.value}</div>
      ),
    },
    {
      field: "categoryName",
      headerName: "Category Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const { originalCategory } = params.row;
        const displayImage = originalCategory.full_profile_image_url ? (
          <img
            src={originalCategory.full_profile_image_url}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "50%",
            }}
          />
        ) : (
          <ImageOutlined sx={{ fontSize: 18, color: "#757575" }} />
        );
        return (
          <div className="d-flex align-items-center">
            <div
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #eee",
                width: 30,
                height: 30,
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginRight: "8px",
                overflow: "hidden",
              }}
            >
              {displayImage}
            </div>
            <div>
              {params.value}
              {originalCategory.id === PINNED_CATEGORY_ID && (
                <span
                  style={{
                    marginLeft: "8px",
                    backgroundColor: "#2196F3",
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                  }}
                >
                  PINNED
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 350,
      sortable: false,
    },
    {
      field: "Actions",
      headerName: "Action",
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="d-flex justify-content-start align-items-center h-100">
          <IconButton
            size="small"
            sx={{ color: "grey", fontSize: 18 }}
            onClick={(e) => {
              e.stopPropagation();
              handleShowEdit(params.row.originalCategory);
            }}
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleShowDelete(params.row.originalCategory);
            }}
            size="small"
            color="error"
            sx={{ fontSize: 18 }}
          >
            <DeleteOutlineOutlined />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (params.row.id === PINNED_CATEGORY_ID) {
                navigate("/requested");
              } else {
                navigate(`/subcategory`);
              }
            }}
            size="small"
            sx={{ color: "grey", fontSize: 18 }}
          >
            <ArrowRightOutlined />
          </IconButton>
        </div>
      ),
    },
  ];

  const tableRows = categories.map((cat) => ({
    id: cat.id,
    categoryName: cat.name,
    description: cat.description,
    iconBgColor: cat.background_color,
    full_profile_image_url: cat.full_profile_image_url,
    originalCategory: cat,
  }));

  return (
    <>
      <Modal show={show} onHide={handleClose} className="">
        <Modal.Header style={{ backgroundColor: "#D5D5D5" }}>
          <Modal.Title className="text-black">
            {isEditing ? "Edit Category" : "Add New Category"}
          </Modal.Title>

          <IconButton onClick={handleClose} aria-label="close">
            <Add sx={{ transform: "rotate(45deg)" }} />
          </IconButton>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <input
              type="file"
              id="profileImageUpload"
              style={{ display: "none" }}
              accept="image/*"
              onChange={handleImageChange}
            />
            <div className="d-flex justify-content-center mb-4 mt-2">
              <div
                style={{
                  position: "relative",
                  width: "110px",
                  height: "110px",
                }}
              >
                <div
                  className="d-flex justify-content-center align-items-center rounded-circle"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "2px dashed #E0E0E0",
                    color: "#757575",
                    backgroundColor: "#FAFAFA",
                    overflow: "hidden",
                  }}
                >
                  {imagePreviewUrl ? (
                    <img
                      src={imagePreviewUrl}
                      alt="Category Icon"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <ImageOutlined sx={{ fontSize: 40 }} />
                  )}
                </div>
                <div
                  className="position-absolute bg-dark text-white d-flex justify-content-center align-items-center rounded-circle shadow-sm"
                  style={{
                    width: "36px",
                    height: "36px",
                    bottom: "0",
                    right: "0",
                    cursor: "pointer",
                    border: "3px solid white",
                  }}
                  onClick={() =>
                    document.getElementById("profileImageUpload").click()
                  }
                >
                  <PhotoCamera sx={{ fontSize: 18 }} />
                </div>
                {imagePreviewUrl && (
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      backgroundColor: "rgba(255,255,255,0.7)",
                      "&:hover": { backgroundColor: "white" },
                      p: "2px",
                    }}
                    onClick={() => {
                      setSelectedImage(null);
                      setImagePreviewUrl("");
                      const fileInput =
                        document.getElementById("profileImageUpload");
                      if (fileInput) fileInput.value = "";
                      if (isEditing && currentCategory?.full_profile_image_url)
                        setImageWasCleared(true);
                    }}
                  >
                    <DeleteOutlineOutlined fontSize="small" color="action" />
                  </IconButton>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label
                htmlFor="categoryName"
                className="form-label fw-semibold"
                style={{ fontSize: "0.95rem" }}
              >
                Category Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="categoryName"
                className={`form-control p-2 ${
                  errors.categoryName ? "is-invalid" : ""
                }`}
                placeholder="Enter Name"
                style={{
                  borderRadius: "8px",
                  borderColor: "#dee2e6",
                  backgroundColor: "#fff",
                }}
                {...register("categoryName", {
                  required: "Category name is required",
                })}
              />
              {errors.categoryName && (
                <div className="invalid-feedback">
                  {errors.categoryName.message}
                </div>
              )}
            </div>

            <div className="mb-3">
              <label
                htmlFor="categoryBackground"
                className="form-label fw-semibold"
                style={{ fontSize: "0.95rem" }}
              >
                Category Background <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="categoryBackground"
                className={`form-control p-2 ${
                  errors.categoryBackground ? "is-invalid" : ""
                }`}
                placeholder="#RRGGBB"
                style={{
                  borderRadius: "8px",
                  borderColor: "#dee2e6",
                  backgroundColor: "#fff",
                }}
                {...register("categoryBackground", {
                  required: "Background color is required",
                  pattern: {
                    value: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
                    message: "Invalid hex color",
                  },
                })}
              />
              {errors.categoryBackground && (
                <div className="invalid-feedback">
                  {errors.categoryBackground.message}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label
                htmlFor="categoryDescription"
                className="form-label fw-semibold"
                style={{ fontSize: "0.95rem" }}
              >
                Category Description
              </label>
              <textarea
                id="categoryDescription"
                rows={4}
                className="form-control p-3"
                placeholder="Add Description"
                style={{
                  borderRadius: "8px",
                  borderColor: "#dee2e6",
                  resize: "none",
                  backgroundColor: "#fff",
                }}
                {...register("categoryDescription")}
              />
            </div>

            <div className="d-flex justify-content-center gap-3 mt-5">
              <button
                type="button"
                onClick={handleClose}
                className="btn px-5 py-2 fw-semibold"
                style={{
                  backgroundColor: "white",
                  border: "1px solid #E0E0E0",
                  borderRadius: "10px",
                  color: "#757575",
                  minWidth: "150px",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-dark px-5 py-2 fw-semibold"
                style={{ borderRadius: "10px", minWidth: "150px" }}
              >
                {isEditing ? "Save Changes" : "Save"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <DeleteModal
        showDelete={showDeleteModal}
        handleDeleteClose={handleCloseDeleteModal}
        textMessage="Are you sure you want to delete this equipment category? This action cannot be undone."
        deleteFunction={handleDelete}
        itemName={categoryToDelete?.name || ""}
      />

      <div className="m-4">
        <div className="d-flex justify-content-between align-items-center w-100">
          <h3 className="m-0">Equipment</h3>
          <button
            onClick={handleShowCreate}
            className="btn btn-light mx-1 border"
            style={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <Add /> New Category
          </button>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center w-100 my-3">
          <div
            className="input-group flex-nowrap justify-content-end align-items-center rounded-2"
            style={{ width: "400px", maxWidth: "100%" }}
          >
            <input
              type="text"
              className="form-control bg-transparent position-relative rounded"
              placeholder="Search for equipment .."
              value={searchTerm}
              onChange={handleSearch}
              style={{ paddingRight: "40px" }}
            />
            <div className="position-absolute mx-2">
              <Search color="action" />
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mt-2 mt-md-0">
            <span className="text-secondary me-1">View by</span>
            <Select
              className="border"
              size="small"
              value={viewBy}
              onChange={handleViewByChange}
              displayEmpty
              sx={{ minWidth: 150 }}
            >
              <MenuItem
                className="custom-menu-item"
                value="Main Categories"
                key="Main Categories"
              >
                Main Categories
              </MenuItem>
              <MenuItem
                className="custom-menu-item"
                value="Sub Categories"
                key="Sub Categories"
              >
                Sub Categories
              </MenuItem>
              <MenuItem
                className="custom-menu-item"
                value="Models"
                key="Models"
              >
                Models
              </MenuItem>
              <MenuItem
                className="custom-menu-item"
                value="All Equipment"
                key="All Equipment"
              >
                All Equipment
              </MenuItem>
            </Select>
            <div className="d-flex rounded border overflow-hidden">
              <button
                onClick={() => setViewMode("cards")}
                className={`btn ${
                  viewMode === "cards" ? "btn-dark" : "btn-light"
                }`}
                style={{ borderRadius: 0 }}
              >
                <GridView />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`btn ${
                  viewMode === "table" ? "btn-dark" : "btn-light"
                }`}
                style={{ borderRadius: 0 }}
              >
                <FormatListBulletedIcon />
              </button>
            </div>
          </div>
        </div>

        {viewMode === "cards" && (
          <>
            <div className="row pb-4 px-2 border border-1 rounded-2 mx-1 mt-3 g-4 justify-content-start">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <div
                    key={category.id}
                    className="col-12 col-sm-6 col-md-4 col-lg-3 d-flex justify-content-center"
                  >
                    <div
                      onClick={() => {
                        if (category.id === PINNED_CATEGORY_ID) {
                          navigate("/requested");
                        } else {
                          navigate(`/subcategory`);
                        }
                      }}
                      style={{
                        backgroundColor: category.background_color || "#F8F8F8",
                        width: "313px",
                        height: "270px",
                        borderRadius: "16px",
                        border: "1px solid #ededed",
                        padding: "16px",
                        cursor: "pointer",
                        position: "relative",
                        ...(category.id === PINNED_CATEGORY_ID && {
                          border: "2px solid #2196F3",
                          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                          backgroundColor: "#E3F2FD",
                        }),
                      }}
                      className="d-flex flex-column"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowEdit(category);
                          }}
                        >
                          <Edit sx={{ fontSize: 20 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowDelete(category);
                          }}
                        >
                          <DeleteOutlineOutlined sx={{ fontSize: 20 }} />
                        </IconButton>
                      </div>
                      <div className="d-flex flex-column align-items-center text-center flex-grow-1">
                        <div
                          style={{
                            backgroundColor: "#FFFFFF",
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginBottom: "1rem",
                            overflow: "hidden",
                          }}
                        >
                          {category.full_profile_image_url ? (
                            <img
                              src={`${
                                category.full_profile_image_url
                              }?v=${Date.now()}`}
                              alt={category.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <ImageOutlined
                              sx={{ fontSize: 40, color: "#757575" }}
                            />
                          )}
                        </div>
                        <h6 className="fw-bold my-2">{category.name}</h6>
                        <p
                          className="text-secondary"
                          style={{ fontSize: "0.85rem" }}
                        >
                          {category.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12 text-center text-secondary mt-5">
                  No categories found.
                </div>
              )}
            </div>
            {rowCount > 0 && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination
                  count={totalPages}
                  page={paginationModel.page + 1}
                  onChange={(event, value) =>
                    setPaginationModel((prev) => ({ ...prev, page: value - 1 }))
                  }
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </div>
            )}
          </>
        )}

        {viewMode === "table" && (
          <div className="data-grid-small mt-4">
            <Paper sx={{ height: "auto", width: "100%" }}>
              <DataGrid
                autoHeight
                rows={tableRows}
                columns={columns}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 20]}
                sx={{ border: 0 }}
                checkboxSelection
                disableRowSelectionOnClick
                getRowClassName={(params) =>
                  params.row.id === PINNED_CATEGORY_ID ? "pinned-row" : ""
                }
                onRowClick={(params) => {
                  if (params.row.id === PINNED_CATEGORY_ID) {
                    navigate("/requested");
                  } else {
                    navigate(`/subcategory`);
                  }
                }}
              />
            </Paper>
          </div>
        )}
      </div>
    </>
  );
};

export default EqCard;
